"use client";

import { motion } from "framer-motion";
import Link from "@/components/LocalizedLink";
import type { ReactElement } from "react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5 },
  }),
};

const outcomes = [
  {
    title: "Low-latency routes into Western Europe",
    desc: "Direct, high-performance paths into Frankfurt, London, Amsterdam, and other core European digital markets.",
  },
  {
    title: "Scalable capacity on demand",
    desc: "Headroom to absorb surging traffic from enterprise, hyperscaler, and carrier customers across Eastern Europe.",
  },
  {
    title: "Resilient, high-availability backbone",
    desc: "Multiple redundant links on a Tier 1 network, supporting long-term growth with carrier-grade SLAs.",
  },
];

export function ZayoCaseStudyClient(): ReactElement {
  return (
    <main className="min-h-screen pt-20">
      {/* Breadcrumb */}
      <section className="px-4 sm:px-6 lg:px-8 pt-6">
        <div className="mx-auto max-w-4xl">
          <nav className="text-xs text-muted-foreground">
            <Link href="/case-studies" className="hover:text-accent transition-colors">
              Case Studies
            </Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">Zayo Europe</span>
          </nav>
        </div>
      </section>

      {/* Hero */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 network-grid opacity-30" />
        <div className="relative z-10 mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-accent/10 text-eyebrow">
                Partnership Story
              </span>
              <span className="text-xs text-muted font-mono">2025</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground">
              Expanding East–West Connectivity with Zayo Europe
            </h1>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
              Sofia Connect teamed up with Zayo Europe to strengthen its
              international backbone, bringing Tier 1 reach and redundant
              capacity to customers across Southeast Europe and the Caucasus.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Challenge · Solution · Partnership */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="mx-auto max-w-4xl space-y-6">
          {[
            {
              heading: "The Challenge",
              body: "Sofia Connect has established itself as a key wholesale carrier in Central and Eastern Europe, supporting data centres, carriers, and enterprises with vital regional connectivity. But as demand for greater capacity and more direct access to Western Europe's digital hubs increased, the company needed a partner with global reach and proven reliability.",
            },
            {
              heading: "The Partner",
              body: "That's where Zayo Europe came in. By connecting Sofia, Zayo added another key location to its European footprint, and multiple links of redundant connectivity enabled Sofia Connect to deliver a growing, high-availability Tier 1 network — pairing regional expertise with global reach.",
            },
            {
              heading: "The Outcome",
              body: "With Zayo Europe's infrastructure behind the Sofia Connect backbone, customers across Eastern Europe gain faster access to Western European digital markets, scalable capacity to match rising traffic demands, and the operational resilience required for long-term growth.",
            },
          ].map((block, i) => (
            <motion.div
              key={block.heading}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              className="bento-card"
            >
              <h2 className="text-xs font-semibold uppercase tracking-wider text-eyebrow mb-3">
                {block.heading}
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                {block.body}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Outcomes grid */}
      <section className="px-4 sm:px-6 lg:px-8 py-12">
        <div className="mx-auto max-w-4xl">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-2xl sm:text-3xl font-bold text-foreground mb-8"
          >
            What the partnership delivers
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {outcomes.map((o, i) => (
              <motion.div
                key={o.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="bento-card h-full"
              >
                <h3 className="text-base font-semibold text-foreground mb-2">
                  {o.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {o.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CEO pull-quote */}
      <section className="px-4 sm:px-6 lg:px-8 py-12">
        <div className="mx-auto max-w-3xl">
          <motion.figure
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bento-card relative"
          >
            <svg
              className="absolute top-4 left-4 w-8 h-8 text-accent/30"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M9.983 3v7.391c0 5.704-3.731 9.57-8.983 10.609l-.995-2.151c2.432-.917 3.995-3.638 3.995-5.849h-4v-10h9.983zm14.017 0v7.391c0 5.704-3.748 9.571-9 10.609l-.996-2.151c2.433-.917 3.996-3.638 3.996-5.849h-3.983v-10h9.983z" />
            </svg>
            <blockquote className="pt-8 px-2">
              <p className="text-lg sm:text-xl text-foreground leading-relaxed font-light italic">
                &ldquo;Eastern Europe is a fast-growing hub for digital
                innovation and continued investment in infrastructure is
                critical to unlocking its full potential. This partnership is a
                strategic step-change. It&apos;s why this partnership is so
                important in providing high capacity, low latency connectivity
                through a partner that prides itself on service excellence.&rdquo;
              </p>
              <figcaption className="mt-6 text-sm">
                <span className="font-semibold text-foreground">Yuliy Nushev</span>
                <span className="text-muted-foreground">
                  {" "}
                  — CEO, Sofia Connect
                </span>
              </figcaption>
            </blockquote>
          </motion.figure>
        </div>
      </section>

      {/* Closing / wider context */}
      <section className="px-4 sm:px-6 lg:px-8 py-12">
        <div className="mx-auto max-w-3xl text-center">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-base text-muted-foreground leading-relaxed"
          >
            By enabling Sofia Connect to strengthen its international backbone,
            Zayo Europe is helping bridge the gap between East and West — making
            modern digital services more accessible, equitable, and future-ready
            across the region.
          </motion.p>
        </div>
      </section>

      {/* Attribution & sources */}
      <section className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="mx-auto max-w-3xl">
          <div className="bento-card bg-surface-light/50">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-eyebrow mb-3">
              About this story
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              This case study is based on the partnership story originally
              published by Zayo Europe in June 2025. The CEO quote and
              operational details appear in Zayo Europe&apos;s published
              materials.
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <span className="text-muted">Source: </span>
                <a
                  href="https://zayoeurope.com/resources/zayo-europe-enables-sofia-connect-to-expand-east-west-connectivity/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-text underline-offset-2 hover:underline"
                >
                  Zayo Europe — Enabling Sofia Connect to expand East–West
                  connectivity
                </a>
              </li>
              <li>
                <span className="text-muted">Related: </span>
                <a
                  href="https://www.facebook.com/ZayoEurope/videos/-zayo-europe-powers-sofia-connects-expansionwere-proud-to-be-the-partner-of-choi/1529501788288646/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-text underline-offset-2 hover:underline"
                >
                  Zayo Europe powers Sofia Connect&apos;s expansion (video)
                </a>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Connect East and West with Sofia Connect
          </h2>
          <p className="text-muted-foreground mb-8">
            IP Transit, DWDM wavelengths and Ethernet L2VPN across Southeast
            Europe and the Caucasus, plus dark fibre inside the Sofia metro —
            engineered for carriers, hyperscalers, and data centres.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium bg-accent text-on-accent rounded-xl hover:bg-accent/90 transition-colors glow-teal"
            >
              Contact Sales
            </Link>
            <Link
              href="/network"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium border border-border text-foreground rounded-xl hover:bg-surface-light transition-colors"
            >
              Explore the network
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
